import { Checkbox } from '@neo/primitives/base/checkbox'
import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { ScrollArea } from '@neo/primitives/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@neo/primitives/table'
import { Textarea } from '@neo/primitives/textarea'
import { Typography } from '@neo/primitives/typography'
import { pluralize } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
  Loader2,
  MoreVertical,
  Play,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'router-v6'
import { QueryError } from 'v2/api-console/shared/components/query-error'
import { formatDateTime } from 'v2/api-console/shared/utils/date-utils'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { flowService } from 'v2/shared/services/flow-service'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast, successToast } from 'v2/utils/toasts'
import { generateScenariosStream, runSelectedScenariosStream } from '../components/eval/eval-stream'
import {
  type GenerationProgress,
  type GenerationStatus,
  selectIsGenerating,
  useSimulationStore,
} from '../components/eval/simulation-store'
import { useVibeRunActive } from '../components/vibe-agent/use-vibe-run-active'
import { useFlowStore } from '../store'
import type { GenericEdge, GenericNode } from '../types'
import { cleanNodesForExport } from '../utils/node-cleanup'
import {
  AUTO_GENERATE_COUNT,
  AUTO_GENERATE_SCENARIOS_ENABLED,
  RUN_BATCH_MAX,
  SCENARIO_LIMITS,
  TURNS_LIMITS,
} from './eval-constants'
import { ScenarioDetailsSidebar } from './scenario-details-sidebar'
import { filterUserTags } from './scenario-tags'
import { ScenariosLoading } from './simulation-loading'
import {
  type PersistedScenario,
  SCENARIOS_PAGE_SIZE,
  useDeleteScenarioMutation,
  useScenariosQuery,
} from './simulation-tab.queries'
import { SimulationToolbar } from './simulation-toolbar'
import { useTagFilter } from './use-tag-filter'

const persistedScenarioTags = (s: PersistedScenario) => filterUserTags(s.scenario.tags)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const scenarioSavedEventToPersistedScenario = (data: unknown): PersistedScenario | null => {
  if (!isRecord(data)) return null
  const payload = isRecord(data.event_data) ? data.event_data : data
  if (typeof payload.uuid !== 'string' || !isRecord(payload.scenario)) return null

  return {
    uuid: payload.uuid,
    flow_id: typeof payload.flow_id === 'number' ? payload.flow_id : 0,
    scenario: payload.scenario as PersistedScenario['scenario'],
    created_at: typeof payload.created_at === 'string' ? payload.created_at : null,
    updated_at: typeof payload.updated_at === 'string' ? payload.updated_at : null,
  }
}

const scenarioCreatedAtMs = (scenario: PersistedScenario) =>
  scenario.created_at ? Date.parse(scenario.created_at) || 0 : 0

const generationProgressText = (progress: GenerationProgress | null) => {
  if (!progress) return 'Preparing scenario generation...'

  const requested = progress.requested_count
  const planned = progress.planned_count ?? requested
  const saved = progress.saved_count ?? 0
  const total = planned ?? requested
  const generatedLabel =
    total != null
      ? `${saved}/${total} ${saved === 1 ? 'scenario' : 'scenarios'} generated`
      : `${pluralize(saved, 'scenario')} generated`

  switch (progress.stage) {
    case 'planning_started':
    case 'planning_done':
    case 'allocation_started':
      return 'Understanding the flow...'
    case 'allocation_done':
    case 'writing_started':
      return 'Creating scenarios...'
    case 'writer_chunk_done':
      return saved > 0 ? `${generatedLabel}...` : 'Creating scenarios...'
    default:
      return saved > 0 ? `${generatedLabel}...` : 'Generating scenarios...'
  }
}

function useAutoGenerateScenarios({
  enabled,
  isLoading,
  scenarioCount,
  isGenerating,
  onGenerate,
}: {
  enabled: boolean
  isLoading: boolean
  scenarioCount: number
  isGenerating: boolean
  onGenerate: () => void
}) {
  const hasTriggered = useRef(false)

  useEffect(() => {
    if (!enabled) return
    if (!isLoading && scenarioCount === 0 && !isGenerating && !hasTriggered.current) {
      hasTriggered.current = true
      onGenerate()
    }
  }, [enabled, isLoading, scenarioCount, isGenerating, onGenerate])
}

interface ScenariosPanelProps {
  onRunStarted: (runUuid: string) => void
  onViewBatchResults?: () => void
}

export const ScenariosPanel = ({ onRunStarted, onViewBatchResults }: ScenariosPanelProps) => {
  const { id: flowId = '' } = useParams()
  const phloUuid = flowId
  const { isDebuggingMode } = useFeatureFlags()
  const isVibeGenerating = useVibeRunActive()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const generationStatus = useSimulationStore((s) => s.generationStatus)
  const generationProgress = useSimulationStore((s) => s.generationProgress)
  const generationError = useSimulationStore((s) => s.generationError)
  const isGenerating = useSimulationStore(selectIsGenerating)
  const simulationEvents = useSimulationStore((s) => s.events)
  const [isGenerateOpen, setIsGenerateOpen] = useState(false)
  const [maxScenarios, setMaxScenarios] = useState(String(SCENARIO_LIMITS.DEFAULT))
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [maxTurns, setMaxTurns] = useState(String(TURNS_LIMITS.DEFAULT))
  const [detailsScenarioUuid, setDetailsScenarioUuid] = useState<string | null>(null)
  const preGenerateUuidsRef = useRef<Set<string> | null>(null)

  const [page, setPage] = useState(1)
  const queryClient = useQueryClient()
  const { data: scenariosData, isLoading, isError, refetch } = useScenariosQuery(phloUuid, page)
  const [isRetrying, setIsRetrying] = useState(false)
  const deleteScenario = useDeleteScenarioMutation(phloUuid)
  const [deletedScenarioUuids, setDeletedScenarioUuids] = useState<Set<string>>(() => new Set())

  const rawPersistedScenarios = useMemo(() => scenariosData?.scenarios ?? [], [scenariosData])
  const persistedScenarios = useMemo(
    () => rawPersistedScenarios.filter((scenario) => !deletedScenarioUuids.has(scenario.uuid)),
    [deletedScenarioUuids, rawPersistedScenarios],
  )
  const persistedScenarioUuids = useMemo(
    () => new Set(persistedScenarios.map((scenario) => scenario.uuid)),
    [persistedScenarios],
  )
  const streamedScenarios = useMemo(() => {
    const byUuid = new Map<string, PersistedScenario>()
    for (const event of simulationEvents) {
      if (event.eventName !== 'scenario_saved') continue
      const scenario = scenarioSavedEventToPersistedScenario(event.data)
      if (scenario && !deletedScenarioUuids.has(scenario.uuid)) byUuid.set(scenario.uuid, scenario)
    }
    return [...byUuid.values()]
  }, [deletedScenarioUuids, simulationEvents])
  const streamedScenarioCountOutsidePage = useMemo(
    () => streamedScenarios.filter((scenario) => !persistedScenarioUuids.has(scenario.uuid)).length,
    [persistedScenarioUuids, streamedScenarios],
  )

  const scenarios = useMemo(() => {
    if (streamedScenarios.length === 0) return persistedScenarios

    const byUuid = new Map<string, PersistedScenario>()
    for (const scenario of persistedScenarios) byUuid.set(scenario.uuid, scenario)
    for (const scenario of streamedScenarios) byUuid.set(scenario.uuid, scenario)

    return [...byUuid.values()]
      .sort((a, b) => scenarioCreatedAtMs(b) - scenarioCreatedAtMs(a))
      .slice(0, SCENARIOS_PAGE_SIZE)
  }, [persistedScenarios, streamedScenarios])
  const knownDeletedPersistedCount = useMemo(
    () =>
      rawPersistedScenarios.filter((scenario) => deletedScenarioUuids.has(scenario.uuid)).length,
    [deletedScenarioUuids, rawPersistedScenarios],
  )
  const persistedTotal = Math.max(0, (scenariosData?.total ?? 0) - knownDeletedPersistedCount)
  const total = isGenerating
    ? persistedTotal + streamedScenarioCountOutsidePage
    : Math.max(persistedTotal, scenarios.length)
  const totalPages = Math.ceil(total / SCENARIOS_PAGE_SIZE)
  const detailsScenario =
    detailsScenarioUuid != null
      ? (scenarios.find((s) => s.uuid === detailsScenarioUuid) ?? null)
      : null

  useEffect(() => {
    if (!preGenerateUuidsRef.current || isGenerating || scenarios.length === 0) return
    const preExisting = preGenerateUuidsRef.current
    const newUuids =
      streamedScenarios.length > 0
        ? streamedScenarios.map((s) => s.uuid)
        : scenarios.filter((s) => !preExisting.has(s.uuid)).map((s) => s.uuid)
    if (newUuids.length === 0) return
    setSelected(new Set(newUuids))
    preGenerateUuidsRef.current = null
  }, [isGenerating, scenarios, streamedScenarios])

  const {
    tagOptions,
    selectedTags,
    setSelectedTags,
    filtered: filteredScenarios,
  } = useTagFilter(scenarios, persistedScenarioTags)

  const allSelected =
    filteredScenarios.length > 0 && filteredScenarios.every((s) => selected.has(s.uuid))
  const someSelected =
    filteredScenarios.length > 0 &&
    filteredScenarios.some((s) => selected.has(s.uuid)) &&
    !allSelected

  const activeRunUuid = useSimulationStore((s) => s.activeRunUuid)
  const [waitingForRunUuid, setWaitingForRunUuid] = useState(false)

  useEffect(() => {
    if (waitingForRunUuid && activeRunUuid) {
      setWaitingForRunUuid(false)
      onRunStarted(activeRunUuid)
    }
  }, [waitingForRunUuid, activeRunUuid, onRunStarted])

  const toggleSelect = useCallback((uuid: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        for (const s of filteredScenarios) next.delete(s.uuid)
      } else {
        for (const s of filteredScenarios) next.add(s.uuid)
      }
      return next
    })
  }, [allSelected, filteredScenarios])

  const markScenariosDeleted = useCallback((uuids: string[]) => {
    const uuidSet = new Set(uuids)
    if (uuidSet.size === 0) return

    setDeletedScenarioUuids((prev) => {
      const next = new Set(prev)
      for (const uuid of uuidSet) next.add(uuid)
      return next
    })
    setSelected((prev) => {
      const next = new Set(prev)
      for (const uuid of uuidSet) next.delete(uuid)
      return next
    })
    setDetailsScenarioUuid((current) => (current && uuidSet.has(current) ? null : current))
  }, [])

  const restoreScenariosDeleted = useCallback((uuids: string[]) => {
    const uuidSet = new Set(uuids)
    if (uuidSet.size === 0) return

    setDeletedScenarioUuids((prev) => {
      const next = new Set(prev)
      for (const uuid of uuidSet) next.delete(uuid)
      return next
    })
  }, [])

  const buildFlowJson = useCallback(() => {
    const { flowInstance, systemPrompt, agentSettings, currentFlowName } = useFlowStore.getState()
    const nodes = (flowInstance?.getNodes() ?? []) as GenericNode[]
    const edges = (flowInstance?.getEdges() ?? []) as GenericEdge[]
    return {
      nodes: cleanNodesForExport(nodes),
      edges,
      system_prompt: systemPrompt || null,
      agent_settings: agentSettings || null,
      flow_name: currentFlowName || null,
    }
  }, [])

  const triggerGenerate = useCallback(
    (count: number, info?: string) => {
      const currentScenarios = scenariosData?.scenarios ?? []
      preGenerateUuidsRef.current = new Set(currentScenarios.map((s) => s.uuid))
      setIsGenerateOpen(false)
      setPage(1)
      const flowJson = buildFlowJson()
      generateScenariosStream(phloUuid, flowJson, count, info)
    },
    [phloUuid, buildFlowJson, scenariosData],
  )

  // React to terminal generation state (set by either the manual SSE or the
  // Vibe-Agent-driven evaluation_update bridge). The ref dedupes the
  // transition so we only fire toasts/refetch once per generation run.
  const prevGenerationStatusRef = useRef<GenerationStatus>('idle')
  useEffect(() => {
    const prev = prevGenerationStatusRef.current
    prevGenerationStatusRef.current = generationStatus
    if (prev === generationStatus) return
    if (generationStatus === 'completed') {
      setPage(1)
      queryClient.invalidateQueries({
        queryKey: flowQueryKeys().simulationScenarios(phloUuid),
      })
      const saved = generationProgress?.saved_count ?? 0
      successToast({
        title: 'Scenarios generated',
        description: saved
          ? `${pluralize(saved, 'scenario')} generated successfully.`
          : 'Scenarios generated successfully.',
      })
    } else if (generationStatus === 'error') {
      errorToast({
        title: 'Generation failed',
        description: generationError ?? '',
      })
    }
  }, [generationStatus, generationError, generationProgress, phloUuid, queryClient])

  useAutoGenerateScenarios({
    enabled: AUTO_GENERATE_SCENARIOS_ENABLED,
    isLoading,
    scenarioCount: total,
    isGenerating,
    onGenerate: useCallback(() => triggerGenerate(AUTO_GENERATE_COUNT), [triggerGenerate]),
  })

  const handleGenerate = useCallback(() => {
    const parsedMax = Number.parseInt(maxScenarios, 10)
    if (
      !Number.isInteger(parsedMax) ||
      parsedMax < SCENARIO_LIMITS.MIN ||
      parsedMax > SCENARIO_LIMITS.MAX
    ) {
      errorToast({
        title: `Scenario count must be between ${SCENARIO_LIMITS.MIN} and ${SCENARIO_LIMITS.MAX}`,
      })
      return
    }
    triggerGenerate(parsedMax, additionalInfo)
  }, [maxScenarios, additionalInfo, triggerGenerate])

  const handleRun = useCallback(() => {
    if (selected.size === 0) {
      errorToast({ title: 'Select at least one scenario to run' })
      return
    }
    const parsedTurns = Number.parseInt(maxTurns, 10)
    if (
      !Number.isInteger(parsedTurns) ||
      parsedTurns < TURNS_LIMITS.MIN ||
      parsedTurns > TURNS_LIMITS.MAX
    ) {
      errorToast({ title: `Max turns must be between ${TURNS_LIMITS.MIN} and ${TURNS_LIMITS.MAX}` })
      return
    }
    if (selected.size > RUN_BATCH_MAX) {
      errorToast({ title: `Select up to ${RUN_BATCH_MAX} scenarios per run` })
      return
    }

    const flowJson = buildFlowJson()
    useSimulationStore.getState().clear()
    setWaitingForRunUuid(true)

    runSelectedScenariosStream(phloUuid, Array.from(selected), flowJson, parsedTurns)
  }, [selected, maxTurns, phloUuid, buildFlowJson])

  const [isDeleting, setIsDeleting] = useState(false)

  const handleDeleteScenario = useCallback(
    (uuid: string) => {
      markScenariosDeleted([uuid])
      deleteScenario.mutate(uuid, {
        onError: () => {
          restoreScenariosDeleted([uuid])
          errorToast({ title: 'Failed to delete scenario' })
        },
      })
    },
    [deleteScenario, markScenariosDeleted, restoreScenariosDeleted],
  )

  const handleDeleteSelected = useCallback(async () => {
    if (selected.size === 0) return
    const deletedUuids = Array.from(selected)
    markScenariosDeleted(deletedUuids)
    setIsDeleting(true)
    try {
      await flowService.batchDeleteSimulationScenarios(deletedUuids)
      setPage(1)
      queryClient.invalidateQueries({
        queryKey: flowQueryKeys().simulationScenarios(phloUuid),
      })
      successToast({ title: `Deleted ${deletedUuids.length} scenarios` })
    } catch {
      restoreScenariosDeleted(deletedUuids)
      errorToast({ title: 'Failed to delete scenarios' })
    } finally {
      setIsDeleting(false)
    }
  }, [markScenariosDeleted, phloUuid, queryClient, restoreScenariosDeleted, selected])

  const panelRef = useRef<HTMLDivElement>(null)
  const handleOpenDetails = useCallback((uuid: string) => {
    setDetailsScenarioUuid((current) => (current === uuid ? null : uuid))
    panelRef.current?.focus()
  }, [])
  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!detailsScenarioUuid) return
      const t = e.target as HTMLElement | null
      const inInteractiveControl = !!t?.closest?.(
        'button, [role="button"], a[href], input, textarea, select, [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], [contenteditable="true"]',
      )
      if (e.key === 'Escape') {
        e.preventDefault()
        setDetailsScenarioUuid(null)
        return
      }
      if (inInteractiveControl) return
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault()
        toggleSelect(detailsScenarioUuid)
        return
      }
      if (e.key === 'Enter') {
        if (selected.size === 0) return
        e.preventDefault()
        handleRun()
        return
      }
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      const idx = filteredScenarios.findIndex((s) => s.uuid === detailsScenarioUuid)
      if (idx < 0) return
      const nextIdx = e.key === 'ArrowDown' ? idx + 1 : idx - 1
      if (nextIdx < 0 || nextIdx >= filteredScenarios.length) return
      e.preventDefault()
      setDetailsScenarioUuid(filteredScenarios[nextIdx].uuid)
      requestAnimationFrame(() => {
        panelRef.current
          ?.querySelector<HTMLElement>('[data-active="true"]')
          ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    },
    [detailsScenarioUuid, filteredScenarios, selected.size, toggleSelect, handleRun],
  )

  return (
    <section
      ref={panelRef}
      tabIndex={-1}
      aria-label="Test scenarios"
      className="flex flex-col h-full outline-none"
      onKeyDown={handlePanelKeyDown}
    >
      <SimulationToolbar
        activeTab="scenarios"
        onTabChange={(tab) => tab === 'results' && onViewBatchResults?.()}
        tagOptions={tagOptions}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
      >
        {selected.size > 0 && (
          <Button
            variant="outline"
            size="sm"
            disabled={isDeleting}
            onClick={handleDeleteSelected}
            className="text-destructive"
          >
            {isDeleting ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5 mr-1" />
            )}
            {isDeleting ? 'Deleting...' : `Delete (${selected.size})`}
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          disabled={isGenerating || isVibeGenerating}
          disabledReason={isVibeGenerating ? 'Vibe Agent is working' : undefined}
          onClick={() => setIsGenerateOpen(true)}
        >
          {isGenerating && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
          {isGenerating ? 'Generating...' : 'Generate Scenarios'}
        </Button>

        <div className="inline-flex">
          <Button
            size="sm"
            disabled={selected.size === 0 || isVibeGenerating}
            disabledReason={isVibeGenerating ? 'Vibe Agent is working' : 'Select scenarios to run'}
            onClick={handleRun}
            className="gap-1.5 rounded-r-none"
            data-testid="button-run-simulations"
          >
            <Play className="h-3.5 w-3.5" />
            Run Simulations
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                disabled={selected.size === 0 || isVibeGenerating}
                disabledReason={
                  isVibeGenerating ? 'Vibe Agent is working' : 'Select scenarios to run'
                }
                className="rounded-l-none border-l border-l-primary-foreground/20 px-1.5"
                aria-label="Run options"
                data-testid="button-run-options"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <div className="flex flex-col gap-3">
                <div>
                  <Label htmlFor="run-max-turns">Max turns per scenario</Label>
                  <Input
                    id="run-max-turns"
                    type="number"
                    min={TURNS_LIMITS.MIN}
                    max={TURNS_LIMITS.MAX}
                    value={maxTurns}
                    onChange={(e) => setMaxTurns(e.target.value)}
                  />
                </div>
                <Button size="sm" onClick={handleRun}>
                  <Play className="h-3.5 w-3.5 mr-1" />
                  Run with {maxTurns} max turns
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </SimulationToolbar>

      {/* Content */}
      <div className="flex-1 min-h-0 p-4">
        {(isError || isRetrying) && (
          <div className="mb-3">
            <QueryError
              title="Couldn't load test scenarios."
              testId="button-retry-test-scenarios"
              isRetrying={isRetrying}
              onRetry={async () => {
                setIsRetrying(true)
                try {
                  await refetch()
                } finally {
                  setIsRetrying(false)
                }
              }}
            />
          </div>
        )}
        {isLoading ? (
          <ScenariosLoading />
        ) : (isError || isRetrying) && scenarios.length === 0 ? null : scenarios.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card text-secondary">
            {isGenerating ? (
              <>
                <Loader2 className="h-10 w-10 animate-spin" />
                <Typography variant="P/400">
                  {generationProgressText(generationProgress)}
                </Typography>
              </>
            ) : (
              <>
                <FlaskConical className="h-10 w-10" />
                <Typography variant="P/400">
                  No scenarios yet. Generate your first batch.
                </Typography>
                <Button variant="outline" onClick={() => setIsGenerateOpen(true)}>
                  <FlaskConical className="h-4 w-4 mr-1" />
                  Generate Scenarios
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="flex h-full overflow-hidden rounded-lg border border-border bg-card">
            <div className="flex flex-1 min-w-0 flex-col">
              <ScrollArea className="flex-1 min-h-0">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="w-[40px] px-6">
                        <Checkbox
                          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                          onCheckedChange={toggleAll}
                          aria-label="Select all"
                          data-testid="checkbox-select-all-scenarios"
                        />
                      </TableHead>
                      <TableHead className="text-secondary h-10">Test Scenarios</TableHead>
                      <TableHead className="text-secondary h-10">Caller Objective</TableHead>
                      <TableHead className="text-secondary h-10 w-[170px]">Created on</TableHead>
                      <TableHead className="w-[40px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredScenarios.map((s) => (
                      <ScenarioRow
                        key={s.uuid}
                        scenario={s}
                        isSelected={selected.has(s.uuid)}
                        isActive={detailsScenarioUuid === s.uuid}
                        onToggleSelect={() => toggleSelect(s.uuid)}
                        onOpenDetails={() => handleOpenDetails(s.uuid)}
                        onDelete={() => handleDeleteScenario(s.uuid)}
                      />
                    ))}
                  </TableBody>
                </Table>

                {isGenerating && (
                  <div className="flex items-center gap-2 px-6 py-3 text-secondary">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <Typography variant="S/400">
                      {generationProgressText(generationProgress)}
                    </Typography>
                  </div>
                )}
              </ScrollArea>

              <div className="flex items-center justify-between border-t border-border px-6 py-3">
                <Typography variant="XS/400" className="font-jetbrains tabular-nums text-secondary">
                  {(page - 1) * SCENARIOS_PAGE_SIZE + 1}-
                  {Math.min(page * SCENARIOS_PAGE_SIZE, total)} of {total}
                </Typography>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            {detailsScenario && (
              <ScenarioDetailsSidebar
                scenario={detailsScenario}
                showJsonTab={isDebuggingMode}
                onClose={() => setDetailsScenarioUuid(null)}
              />
            )}
          </div>
        )}
      </div>

      {/* Generate dialog */}
      <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
        <DialogContent className="max-w-lg" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Test Scenario Generation</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="gen-total-scenarios">Total Scenarios to test</Label>
              <Input
                id="gen-total-scenarios"
                type="number"
                min={SCENARIO_LIMITS.MIN}
                max={SCENARIO_LIMITS.MAX}
                value={maxScenarios}
                onChange={(e) => setMaxScenarios(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="gen-additional-info">
                Additional Information{' '}
                <span className="italic text-muted-foreground">(Optional)</span>
              </Label>
              <Textarea
                id="gen-additional-info"
                placeholder="Enter any additional details here, ex: test angry customer scenarios"
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setIsGenerateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerate} disabled={isGenerating}>
              {isGenerating ? 'Generating...' : 'Generate Scenarios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

const ScenarioRow = ({
  scenario,
  isSelected,
  isActive,
  onToggleSelect,
  onOpenDetails,
  onDelete,
}: {
  scenario: PersistedScenario
  isSelected: boolean
  isActive: boolean
  onToggleSelect: () => void
  onOpenDetails: () => void
  onDelete: () => void
}) => {
  const { name, goal } = scenario.scenario
  const createdAt = scenario.created_at
    ? formatDateTime(scenario.created_at, { format: 'D MMM YYYY [at] HH:mm' })
    : '-'

  return (
    <TableRow
      data-active={isActive}
      tabIndex={0}
      aria-selected={isActive}
      className="group cursor-pointer border-border transition-colors hover:bg-hv1 data-[active=true]:bg-brand/10 focus-visible:bg-hv1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
      onClick={onOpenDetails}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpenDetails()
        }
      }}
    >
      <TableCell className="px-6 w-[40px]">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelect}
          aria-label={`Select ${name ?? ''}`}
          onClick={(e) => e.stopPropagation()}
        />
      </TableCell>
      <TableCell>
        <Typography variant="S/400">{name ?? 'Untitled'}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="S/400" className="text-secondary line-clamp-3">
          {goal}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="XS/400" className="text-secondary font-jetbrains tabular-nums">
          {createdAt}
        </Typography>
      </TableCell>
      <TableCell className="w-[40px]">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4 text-secondary" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-destructive"
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}
